import { format, getDay, parseISO } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

const englishDayToHebrew = (dayIndex) => {
  switch (dayIndex) {
    case 0: // Sunday
      return 'ראשון';
    case 1: // Monday
      return 'שני';
    case 2: // Tuesday
      return 'שלישי';
    case 3: // Wednesday
      return 'רביעי';
    case 4: // Thursday
      return 'חמישי';
    case 5: // Friday
      return 'שישי';
    case 6: // Saturday
      return 'שבת';
    default:
      return '';
  }
};

const ObituaryDisplay = ({ properties }) => {
  const { t } = useTranslation();
  const {
    name,
    suffix,
    birthdate,
    deathdate,
    grieveWords,
    closeness,
    hasMemorial,
    memorialDate,
    memorialHour,
    memorialPlace,
    hebrewDate,
    hasSeven,
    sevenStartDate,
    sevenEndDate,
    sevenStartHour,
    sevenEndHour,
    familyLocation,
    familyName,
  } = properties;

  const hasBothDates = !!birthdate && !!deathdate;
  const memorialDateObj = memorialDate ? parseISO(memorialDate) : null;

  let hoursPrefix;
  if (!!sevenEndHour && !!sevenStartHour) {
    hoursPrefix = t('obituary.display.hoursBetween');
  } else if (sevenStartHour) {
    hoursPrefix = t('obituary.display.hoursFrom');
  } else if (sevenEndHour) {
    hoursPrefix = t('obituary.display.hoursUntil');
  }

  const hasContent = name || grieveWords || birthdate || deathdate || hasMemorial || hasSeven;

  return (
    <div className="overflow-y-auto white p-6 border-solid border-[#063447] border-[20px] rounded-lg rounded-t-none sm:min-h-[30em] md:h-fit h-full">
      {!hasContent ? (
        <div className="flex items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-stone-600">{t('obituary.preview.empty')}</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center w-full">
          <div className="w-full max-w-lg bg-white rounded-lg border-2 border-stone-300 p-10 space-y-5">
            <div className="space-y-4">
              {grieveWords && (
                <div className="text-center text-lg leading-relaxed">
                  {closeness ? `${grieveWords} ${closeness}` : grieveWords}
                </div>
              )}

              {name && (
                <div className="flex items-baseline justify-center gap-1">
                  <h2 className="text-3xl font-semibold">{name}</h2>
                  {suffix && <span className="text-3xl">{suffix}</span>}
                </div>
              )}

              {hebrewDate && (
                <div className="text-center text-base font-light">
                  {`${t('obituary.display.hebrewDatePrefix')} ${hebrewDate}`}
                </div>
              )}

              {(birthdate || deathdate) && (
                <div className="text-center text-lg">
                  {birthdate?.split('-').reverse().join('/')}
                  {hasBothDates && ' - '}
                  {deathdate?.split('-').reverse().join('/')}
                </div>
              )}

              {hasMemorial && (
                <div className="text-center space-y-2 pt-4 border-t border-stone-200">
                  <h3 className="text-xl font-semibold mb-2">
                    {t('obituary.display.funeralWillBeHeld')}
                  </h3>
                  {memorialDate && memorialDateObj && (
                    <p className="text-base">
                      {`יום ${englishDayToHebrew(getDay(memorialDateObj))} בתאריך ${format(
                        memorialDateObj,
                        'dd/MM/yyyy'
                      )}`}
                    </p>
                  )}
                  {memorialHour && (
                    <p className="text-base">{`${t('obituary.display.atTime')}${memorialHour}`}</p>
                  )}
                  {memorialPlace && <p className="text-base">{memorialPlace}</p>}
                </div>
              )}

              {hasSeven && (
                <div className="text-center space-y-2 pt-4 border-t border-stone-200">
                  <h3 className="text-xl font-semibold mb-2">
                    {t('obituary.display.shivaWillBeHeld')}
                  </h3>
                  {(sevenStartDate || sevenEndDate) && (
                    <p className="text-base">
                      {sevenStartDate?.split('-').reverse().join('/')}
                      {sevenStartDate && sevenEndDate && ' - '}
                      {sevenEndDate?.split('-').reverse().join('/')}
                    </p>
                  )}
                  {(sevenStartHour || sevenEndHour) && (
                    <p className="text-base">
                      {hoursPrefix}
                      {sevenStartHour}
                      {sevenStartHour && sevenEndHour && ' - '}
                      {sevenEndHour}
                    </p>
                  )}
                  {familyName && (
                    <p className="text-base">
                      {t('obituary.display.atFamilyHome')} {familyName}
                    </p>
                  )}
                  {familyLocation && <p className="text-base">{familyLocation}</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ObituaryDisplay;
